import React, { useState } from 'react';
import moment from 'moment';
import { Box, Tooltip } from '@mui/material';
import { useSnackbar } from 'notistack';
import { GridColumns, GridSortModel } from '@mui/x-data-grid';
import { ExtendIcon } from '@/common/icons/ExtendIcon';
import ExtendCreditsModal from './ExtendCreditsModal';
import { callRestGetEndpoint } from '@/common/utils/api';
import { getOrderBy } from '@/RestoreComponent/DataGrid/utils';
import { convertToStoreLocalTime } from '@/common/utils/time';
import { isUnauthorizedStudio } from '@/common/utils/creditUtils';
import AuthUserStore from '@/common/utils/AuthUser';
import { RestoreDataGrid } from '@/RestoreComponent/DataGrid';

const initialSort: GridSortModel = [{ field: 'expires', sort: 'asc' }];

export default function ExpiredCredits({
  customerAllData
}: ExpiredCreditsProps) {
  const { enqueueSnackbar } = useSnackbar();
  const currentUserStoreId = AuthUserStore.getStoreId()?.toString();

  const [credits, setCredits] = useState<Credit[]>([]);
  const [extendModalOpen, setExtendModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<Credit | null>(null);

  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [pageDirection, setPageDirection] = useState(1);
  const [pageInfo, setPageInfo] = useState({
    endCursor: undefined
  });
  const [sortOptions, setSortOptions] = useState(initialSort);
  const [totalCount, setTotalCount] = useState(0);

  const getQueryParams = (
    customerAllData: CustomerData,
    sortOptions: GridSortModel,
    pageSize: number,
    page: number,
    pageInfo: any,
    pageDirection: number
  ) => {
    const params: Record<string, any> = {
      customer: customerAllData?.id,
      orderBy: getOrderBy(sortOptions)
    };
    if (page != 0) {
      if (pageDirection == 1) {
        params['after'] = pageInfo?.endCursor;
        params['first'] = pageSize;
      } else {
        params['before'] = pageInfo?.startCursor;
        params['last'] = pageSize;
      }
    } else {
      params['first'] = pageSize;
    }
    return params;
  };

  const getExpiredCredits = async () => {
    setLoading(true);
    try {
      const res = await callRestGetEndpoint(
        getQueryParams(
          customerAllData,
          sortOptions,
          pageSize,
          page,
          pageInfo,
          pageDirection
        ),
        '/pos_customer/credits/expired/'
      );

      // Process the response to fix the ids field format
      const processedData = res.data.map((credit: any) => ({
        ...credit,
        ids:
          typeof credit.ids === 'string' ? JSON.parse(credit.ids) : credit.ids
      }));

      setCredits(processedData);
      setTotalCount(res?.totalCount || 0);
      setPageInfo(res?.pageInfo || { endCursor: undefined });
    } catch (e) {
      enqueueSnackbar('A problem was encountered fetching expired credits.', {
        variant: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    getExpiredCredits();
  }, [page, pageSize, sortOptions]);

  const isExtendDisabled = (row: Credit) => {
    const originalExpiry = row.original_expiry || row.expires;
    const maxExtensionDate = moment(originalExpiry).add(60, 'days');
    const creditAge = moment().diff(moment(row.issue_date), 'months');
    const isOutsideExtensionWindow =
      moment().diff(moment(row.expires), 'days') > 60;
    const unauthorized = isUnauthorizedStudio(
      row.purchase_store_id,
      currentUserStoreId
    );

    if (moment(row.expires).isAfter(moment())) {
      return false;
    }

    if (creditAge >= 12) {
      return true;
    }

    return (
      moment(row.expires).isSameOrAfter(maxExtensionDate) ||
      row.is_refunded ||
      isOutsideExtensionWindow ||
      unauthorized
    );
  };

  const getExtendTooltip = (row: Credit) => {
    const originalExpiry = row.original_expiry || row.expires;
    const maxExtensionDate = moment(originalExpiry).add(60, 'days');
    const creditAge = moment().diff(moment(row.issue_date), 'months');
    const isOutsideExtensionWindow =
      moment().diff(moment(row.expires), 'days') > 60;
    const unauthorized = isUnauthorizedStudio(
      row.purchase_store_id,
      currentUserStoreId
    );

    if (moment(row.expires).isSameOrAfter(maxExtensionDate)) {
      return 'Maximum extension reached (60 days from original expiry)';
    }
    if (creditAge >= 12) {
      return 'Credit exceeds age limit (12 months)';
    }
    if (row.is_refunded) {
      return 'Credit was refunded';
    }
    if (isOutsideExtensionWindow) {
      return 'Outside extension window (60 days after expiry)';
    }
    if (unauthorized) {
      return 'Unauthorized studio';
    }
    return 'Extend credit expiry date';
  };

  const handleExtend = (row: Credit) => {
    setSelectedRow(row);
    setExtendModalOpen(true);
  };

  const columnConfig: GridColumns = [
    {
      field: 'quantity',
      headerName: 'Quantity',
      flex: 0.4,
      sortable: false
    },
    {
      field: 'issue_date',
      headerName: 'Date Issued',
      flex: 0.7,
      sortable: false,
      renderCell: ({ row }) =>
        convertToStoreLocalTime(row.issue_date, 'MM/DD/YYYY')
    },
    {
      field: 'source',
      headerName: 'Source',
      flex: 1,
      sortable: false
    },
    {
      field: 'description',
      headerName: 'Description',
      flex: 1,
      sortable: false
    },
    {
      field: 'expires',
      headerName: 'Last Expiry Date',
      flex: 0.7,
      sortable: false,
      renderCell: ({ row }) =>
        convertToStoreLocalTime(row.expires, 'MM/DD/YYYY')
    },
    {
      field: 'reason',
      headerName: 'Reason',
      flex: 1,
      sortable: false,
      renderCell: ({ row }) => {
        const reasonText = row?.last_update_reason || '';
        return <div className="ellipsify" title={reasonText}>{reasonText}</div>;
      }
    },
    {
      field: '_extend_action',
      headerName: 'Extend',
      width: 60,
      sortable: false,
      renderCell: ({ row }) => (
        <Box display="flex" justifyContent="center" alignItems="center">
          <Tooltip title={getExtendTooltip(row)}>
            <span>
              <ExtendIcon
                style={{
                  cursor: isExtendDisabled(row) ? 'not-allowed' : 'pointer',
                  opacity: isExtendDisabled(row) ? 0.5 : 1
                }}
                onClick={() => !isExtendDisabled(row) && handleExtend(row)}
              />
            </span>
          </Tooltip>
        </Box>
      )
    }
  ];

  return (
    <>
      <RestoreDataGrid
        rowCount={totalCount}
        columnConfig={columnConfig}
        rows={credits}
        loading={loading}
        page={page}
        pageSize={pageSize}
        pageSizes={[10, 50, 100]}
        sortModel={sortOptions}
        onPageChange={(p, direction) => {
          setPage(p);
          setPageDirection(direction);
        }}
        onPageChangeSize={ps => {
          setPageSize(ps);
          setPage(0);
        }}
        onSortModelChange={model => setSortOptions(model)}
      />
      {extendModalOpen && selectedRow && (
        <ExtendCreditsModal
          open={extendModalOpen}
          onClose={() => {
            setExtendModalOpen(false);
            setSelectedRow(null);
          }}
          bundleName={selectedRow?.source || ''}
          ingredient={selectedRow?.description || ''}
          expiryDate={selectedRow?.expires || ''}
          onSave={() => {
            setExtendModalOpen(false);
            setSelectedRow(null);
            getExpiredCredits();

            enqueueSnackbar(
              'Credit successfully reactivated\nReview it in Remaining Credits now.',
              {
                variant: 'success',
                style: { whiteSpace: 'pre-line' }
              }
            );
          }}
          mode="extend"
          selectedCredit={selectedRow}
          customerId={customerAllData?.id}
        />
      )}
    </>
  );
}
